/**
 * LessonsPage - Tutorial/lesson view for learning about prediction markets.
 * No simulation speed controls - those are only on the sandbox page.
 */

import { useState, useCallback, useMemo } from 'react'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '../lib/utils'
import { useAppStore } from '../store/app'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { LessonView } from '../components/curriculum/LessonView'
import { OrderBookChart } from '../components/visualizations/OrderBookChart'
import { DepthChart } from '../components/visualizations/DepthChart'
import { PriceHistory } from '../components/visualizations/PriceHistory'
import { ProbabilityDisplay } from '../components/visualizations/ProbabilityDisplay'
import { TradingPanel } from '../components/trading/TradingPanel'
import { TradeExecutionBreakdown } from '../components/trading/TradeExecutionBreakdown'
import { OrderTimeline } from '../components/visualizations/OrderTimeline'
import {
  getAllLessons,
  getLesson,
  isLessonUnlocked,
  getLessonProgress,
  getNextLesson,
} from '../curriculum/manager'

/**
 * Collapsible section component for mobile layout.
 */
function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
  className,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
  className?: string
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className={cn('rounded-lg border border-border bg-card', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-3 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium text-foreground">{title}</span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isOpen && <div className="border-t border-border p-3">{children}</div>}
    </div>
  )
}

export function LessonsPage() {
  useDocumentMeta({
    title: 'Prediction Market Tutor — Interactive Lessons',
    description: 'Learn how prediction markets work through interactive lessons. Understand order books, market orders, limit orders, and price discovery — no experience required.',
    canonical: 'https://learnprediction.markets/',
  })

  // Store state
  const {
    currentLessonId,
    completedLessons,
    market,
    balance,
    positions,
    openOrders,
    visualizationMode,
    debugMode,
    lastExecution,
    timelineEvents,
    startLesson,
    completeLesson,
    placeOrder,
    cancelOrder,
    setVisualizationMode,
    setDebugMode,
    skipToLesson,
    clearLastExecution,
  } = useAppStore()

  // Local UI state
  const [selectedPrice, setSelectedPrice] = useState<number | undefined>()

  // Get curriculum data
  const allLessons = useMemo(() => getAllLessons(), [])
  const currentLesson = currentLessonId ? getLesson(currentLessonId) : undefined
  const progress = getLessonProgress(completedLessons)
  const currentLessonScenario = currentLesson?.content.find(
    (content) => content.type === 'interactive' || content.type === 'sandbox'
  )

  // Find current lesson index
  const currentLessonIndex = currentLesson
    ? allLessons.findIndex((l) => l.id === currentLesson.id)
    : -1
  const currentLessonNumber = currentLessonIndex >= 0 ? currentLessonIndex + 1 : 0

  // Navigation state
  const hasPrevious = currentLessonIndex > 0
  const hasNext = currentLessonIndex < allLessons.length - 1
  const nextLesson = hasNext ? allLessons[currentLessonIndex + 1] : undefined
  const isNextUnlocked = nextLesson
    ? isLessonUnlocked(nextLesson.id, completedLessons)
    : false

  // Market state for visualizations
  const orderBook = market?.orderBook
  const trades = orderBook?.trades ?? []
  const currentPrice = orderBook?.lastPrice ?? null

  // Handlers
  const handleToggleMode = useCallback(() => {
    setVisualizationMode(visualizationMode === 'beginner' ? 'advanced' : 'beginner')
  }, [visualizationMode, setVisualizationMode])

  const handleToggleDebugMode = useCallback(() => {
    setDebugMode(!debugMode)
  }, [debugMode, setDebugMode])

  const handlePreviousLesson = useCallback(() => {
    if (hasPrevious) {
      const prevLesson = allLessons[currentLessonIndex - 1]
      startLesson(prevLesson.id)
    }
  }, [hasPrevious, allLessons, currentLessonIndex, startLesson])

  const handleNextLesson = useCallback(() => {
    if (hasNext && isNextUnlocked && nextLesson) {
      startLesson(nextLesson.id)
    }
  }, [hasNext, isNextUnlocked, nextLesson, startLesson])

  const handleLessonComplete = useCallback(
    (lessonId: string) => {
      completeLesson(lessonId)
      // Auto-advance to next lesson if available
      const next = getNextLesson(new Set([...completedLessons, lessonId]))
      if (next) {
        startLesson(next.id)
      }
    },
    [completeLesson, completedLessons, startLesson]
  )

  const handlePriceClick = useCallback((price: number) => {
    setSelectedPrice(price)
  }, [])

  // Enabled features based on lesson (placeholder - will be driven by curriculum)
  const enabledFeatures = {
    limitOrders: true,
    marketOrders: true,
    cancelOrders: true,
  }

  // User order IDs for highlighting in order book
  const userOrderIds = useMemo(
    () => new Set(openOrders.map((o) => o.id)),
    [openOrders]
  )

  // If no lessons registered, show getting started message
  if (allLessons.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header progress={0} />
        <main className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-md text-center">
            <h2 className="text-2xl font-bold text-foreground">
              Welcome to Prediction Market Learner
            </h2>
            <p className="mt-4 text-muted-foreground">
              Lessons are being prepared. Check back soon to start learning about
              prediction markets!
            </p>
          </div>
        </main>
      </div>
    )
  }

  // If no current lesson, start with the first one
  if (!currentLesson) {
    const firstUnlocked = getNextLesson(completedLessons) ?? allLessons[0]
    if (firstUnlocked) {
      startLesson(firstUnlocked.id)
    }
    return null // Re-render will happen after startLesson
  }

  // If we have a lesson but the simulation state does not match it, reload the lesson scenario.
  if (
    currentLessonScenario &&
    (currentLessonScenario.type === 'interactive' || currentLessonScenario.type === 'sandbox') &&
    (!market || market.id !== currentLessonScenario.data.scenario.market.id)
  ) {
    if (currentLesson) {
      startLesson(currentLesson.id)
      return null
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:outline-none"
      >
        Skip to content
      </a>

      {/* Header */}
      <Header
        currentLessonTitle={currentLesson.title}
        currentLessonNumber={currentLessonNumber}
        totalLessons={allLessons.length}
        progress={progress}
        visualizationMode={visualizationMode}
        onToggleMode={handleToggleMode}
        debugMode={debugMode}
        onToggleDebugMode={handleToggleDebugMode}
        allLessons={allLessons}
        onSkipToLesson={skipToLesson}
      />

      {/* Main content area */}
      <main id="main-content" className="flex-1 overflow-hidden lg:overflow-y-auto">
        <div className="mx-auto h-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          {/* Desktop: Two-column layout */}
          <div className="hidden h-[calc(100vh-10rem)] gap-6 lg:grid lg:grid-cols-2">
            {/* Left column: Lesson content */}
            <div className="overflow-y-auto rounded-lg">
              <LessonView
                lesson={currentLesson}
                onComplete={handleLessonComplete}
                orderBook={orderBook}
                currentPrice={currentPrice}
                marketQuestion={market?.question}
                visualizationMode={visualizationMode}
              />
            </div>

            {/* Right column: Visualizations and Trading */}
            <div className="space-y-4 overflow-y-auto pr-2">
              {/* Probability display */}
              {market && (
                <ProbabilityDisplay
                  price={currentPrice}
                  question={market.question}
                />
              )}

              {/* Order book and depth chart */}
              {orderBook && (
                <div className="grid gap-4 xl:grid-cols-2">
                  <OrderBookChart
                    orderBook={orderBook}
                    mode={visualizationMode}
                    onPriceClick={handlePriceClick}
                    highlightedPrice={selectedPrice}
                    userOrderIds={userOrderIds}
                  />
                  <DepthChart orderBook={orderBook} height={200} />
                </div>
              )}

              {/* Activity timeline (smaller for lessons) */}
              {orderBook && timelineEvents.length > 0 && (
                <OrderTimeline
                  events={timelineEvents}
                  height={150}
                  maxEvents={10}
                />
              )}

              {/* Price history */}
              {trades.length > 0 && (
                <PriceHistory trades={trades} height={150} />
              )}

              {/* Trade execution breakdown (shown after market orders with slippage) */}
              {lastExecution && lastExecution.trades.length > 1 && (
                <TradeExecutionBreakdown
                  trades={lastExecution.trades}
                  side={lastExecution.side}
                  totalQuantity={lastExecution.totalQuantity}
                  onDismiss={clearLastExecution}
                />
              )}

              {/* Trading panel */}
              <TradingPanel
                market={market}
                userBalance={balance}
                userPositions={positions}
                openOrders={openOrders}
                currentPrice={currentPrice}
                onPlaceOrder={placeOrder}
                onCancelOrder={cancelOrder}
                enabledFeatures={enabledFeatures}
                selectedPrice={selectedPrice}
              />
            </div>
          </div>

          {/* Mobile/Tablet: Stacked layout with collapsible sections */}
          <div className="space-y-4 lg:hidden">
            {/* Lesson content (always visible) */}
            <LessonView
              lesson={currentLesson}
              onComplete={handleLessonComplete}
              orderBook={orderBook}
              currentPrice={currentPrice}
              marketQuestion={market?.question}
              visualizationMode={visualizationMode}
            />

            {/* Probability display */}
            {market && (
              <ProbabilityDisplay
                price={currentPrice}
                question={market.question}
              />
            )}

            {/* Visualizations (collapsible) */}
            {orderBook && (
              <CollapsibleSection title="Market Visualization" defaultOpen={true}>
                <div className="space-y-4">
                  <OrderBookChart
                    orderBook={orderBook}
                    mode={visualizationMode}
                    onPriceClick={handlePriceClick}
                    highlightedPrice={selectedPrice}
                    userOrderIds={userOrderIds}
                  />
                  <DepthChart orderBook={orderBook} height={180} />
                </div>
              </CollapsibleSection>
            )}

            {/* Price history (collapsible) */}
            {trades.length > 0 && (
              <CollapsibleSection title="Price History" defaultOpen={false}>
                <PriceHistory trades={trades} height={150} />
              </CollapsibleSection>
            )}

            {/* Activity feed (collapsible) */}
            {timelineEvents.length > 0 && (
              <CollapsibleSection title="Activity Feed" defaultOpen={false}>
                <OrderTimeline
                  events={timelineEvents}
                  height={180}
                  maxEvents={15}
                />
              </CollapsibleSection>
            )}

            {/* Trade execution breakdown (shown after market orders with slippage) */}
            {lastExecution && lastExecution.trades.length > 1 && (
              <TradeExecutionBreakdown
                trades={lastExecution.trades}
                side={lastExecution.side}
                totalQuantity={lastExecution.totalQuantity}
                onDismiss={clearLastExecution}
              />
            )}

            {/* Trading panel (collapsible, acts as bottom sheet pattern) */}
            <CollapsibleSection title="Trading" defaultOpen={false}>
              <TradingPanel
                market={market}
                userBalance={balance}
                userPositions={positions}
                openOrders={openOrders}
                currentPrice={currentPrice}
                onPlaceOrder={placeOrder}
                onCancelOrder={cancelOrder}
                enabledFeatures={enabledFeatures}
                selectedPrice={selectedPrice}
              />
            </CollapsibleSection>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer
        currentLessonNumber={currentLessonNumber}
        totalLessons={allLessons.length}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        isNextUnlocked={isNextUnlocked}
        onPrevious={handlePreviousLesson}
        onNext={handleNextLesson}
      />
    </div>
  )
}
